import React, { useState } from 'react';
import AMPageShell from './components/AMPageShell';
import Icon from 'components/AppIcon';
import { ASSETS } from './data/assetData';

const DOC_STATUS_COLOURS = {
  Linked: 'text-success',
  Complete: 'text-success',
  Pending: 'text-warning',
  Current: 'text-success',
  'Pending update': 'text-warning',
};

export default function GovernanceLibrary({ selectedAssets, showToast }) {
  const assets = selectedAssets || ASSETS;
  const [activeAssetId, setActiveAssetId] = useState(assets?.[0]?.id);
  const asset = assets?.find((a) => a?.id === activeAssetId) || assets?.[0];

  if (!asset) return <AMPageShell activeTab="/asset-management/governance-library"><p className="text-muted-foreground font-caption">No assets selected.</p></AMPageShell>;

  const docs = asset?.governanceDocs;

  const docRows = [
    { label: 'Board Papers', value: docs?.boardPapers, icon: 'FolderOpen' },
    { label: 'Financial Model', value: docs?.financialModel, icon: 'FolderOpen' },
    { label: 'Finance Review Attachments', value: docs?.financeReview, icon: 'Paperclip' },
    { label: 'HSE Policy', value: docs?.hsePolicy, icon: 'ShieldCheck' },
    { label: 'ESG Policy', value: docs?.esgPolicy, icon: 'Leaf' },
  ];

  return (
    <AMPageShell activeTab="/asset-management/governance-library">
      <div className="flex gap-6">
        <div className="w-48 flex-shrink-0">
          <div className="bg-card border border-border rounded-lg shadow-xs overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide">Asset</p>
            </div>
            {assets?.map((a) => (
              <button key={a?.id} onClick={() => setActiveAssetId(a?.id)}
                className={`w-full text-left px-4 py-2.5 transition-colors ${
                  a?.id === activeAssetId ? 'bg-primary/5 border-l-2 border-primary' : 'hover:bg-muted/40'
                }`}>
                <p className="font-caption text-sm font-medium text-foreground truncate">{a?.name}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 min-w-0 space-y-5">
          <div className="bg-card border border-border rounded-lg shadow-xs p-6">
            <h2 className="font-heading font-semibold text-foreground mb-4" style={{ fontSize: '1.1rem' }}>{asset?.name} — Governance Library</h2>
            <div className="space-y-2 mb-6">
              {docRows?.map((row) => (
                <div key={row?.label} className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
                  <div className="flex items-center gap-2">
                    <Icon name={row?.icon} size={15} color="var(--color-muted-foreground)" strokeWidth={2} />
                    <span className="font-caption text-sm text-foreground">{row?.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`font-caption text-sm font-medium ${DOC_STATUS_COLOURS?.[row?.value] || 'text-muted-foreground'}`}>{row?.value}</span>
                    <button className="text-xs text-primary hover:underline font-caption">Open</button>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-muted/30 rounded-md p-4 mb-4">
              <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Annual Valuation Assessment Notes</p>
              <p className="font-body text-sm text-foreground leading-relaxed">{docs?.annualValuationNotes}</p>
            </div>

            {/* Consistency check widget */}
            <div className={`rounded-md p-4 border ${
              docs?.consistencyCheck ? 'bg-success/5 border-success/20' : 'bg-warning/5 border-warning/20'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon
                  name={docs?.consistencyCheck ? 'CheckCircle' : 'AlertCircle'}
                  size={16}
                  color={docs?.consistencyCheck ? 'var(--color-success)' : 'var(--color-warning)'}
                  strokeWidth={2}
                />
                <p className="font-caption text-sm font-semibold text-foreground">Consistency Check</p>
                <span className={`ml-auto text-xs font-caption font-semibold ${
                  docs?.consistencyCheck ? 'text-success' : 'text-warning'
                }`}>{docs?.consistencyCheck ? 'Yes — Consistent' : 'No — Review Required'}</span>
              </div>
              <p className="font-body text-sm text-foreground">{docs?.consistencyCommentary}</p>
            </div>
          </div>
        </div>
      </div>
    </AMPageShell>
  );
}
